"use client";

import { useMessages } from "@/lib/i18n/useMessages";
import { Box, Link, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { SLHtmlFromLangFile } from "../sections/home/SLHtmlFromLangFile";

interface SLTruncatedTextProps {
  text: string;
  maxLength?: number;
  variant?: "body1" | "body2" | "caption" | "subtitle1" | "subtitle2";
  showMoreText?: string;
  showLessText?: string;
  color?: string;
  backgroundColor?: string;
}

export const SLTruncatedText = ({
  text,
  maxLength = 150,
  variant = "body1",
  showMoreText,
  showLessText,
  color,
  backgroundColor,
}: SLTruncatedTextProps) => {
  const { common } = useMessages();
  const more = showMoreText ?? common.truncatedText.showMore;
  const less = showLessText ?? common.truncatedText.showLess;
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();

  // Strip HTML tags to calculate actual text length (SSR-safe)
  const stripHtml = (html: string) => html.replace(/<[^>]*>/g, "");

  const plainText = stripHtml(text);

  // If text is shorter than maxLength, show it as is
  if (plainText.length <= maxLength) {
    return (
      <Typography variant={variant} color={color} component="div">
        <SLHtmlFromLangFile htmlString={text} />
      </Typography>
    );
  }

  return (
    <Box>
      {/* Always render full content for SEO, use CSS to hide overflow */}
      <Box
        sx={{
          position: "relative",
          ...(!isExpanded && {
            maxHeight: `${Math.max(3, Math.floor(maxLength / 40)) * 1.4}em`,
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              right: 0,
              width: "70%",
              height: "1.5em",
              background: `linear-gradient(to right, rgba(255, 255, 255, 0), ${backgroundColor ?? theme.palette.background.default} 50%)`,
            },
          }),
        }}
      >
        <Typography variant={variant} color={color} component="div">
          <SLHtmlFromLangFile htmlString={text} />
        </Typography>
      </Box>

      <Link
        component="button"
        variant={variant}
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          cursor: "pointer",
          fontWeight: 600,
          border: "none",
          background: "none",
          padding: 0,
          my: 1.5,
          display: "inline-block",
          color: "primary.main",
          "&:hover": {
            color: "primary.dark",
            textDecoration: "underline",
          },
        }}
      >
        {isExpanded ? less : more}
      </Link>
    </Box>
  );
};
